import { Info } from 'lucide-react'
import { FormDialog } from '@/components/common'
import { Skeleton } from '@/components/ui/skeleton'
import { useExplain } from '../api'

/**
 * Why one figure is the number it is (item 9).
 *
 * <p>Shows the rule that produced it, what the formula could see, and how it got there —
 * not a recomputation. A recomputation would apply today's rules to last year's figure and
 * look authoritative while describing something that never happened.
 */
export function ExplainDialog({
  lineId,
  onClose,
}: {
  lineId: string | null
  onClose: () => void
}) {
  const explained = useExplain(lineId ?? undefined)

  return (
    <FormDialog
      open={lineId != null}
      onOpenChange={(open) => { if (!open) onClose() }}
      size="lg"
      title="Why this figure"
      description="The rule that produced it, and the values it was given."
      submitLabel="Close"
      cancelLabel="Done"
      onSubmit={onClose}
    >
      {explained.isPending && <Skeleton className="h-64 w-full" />}

      {explained.data?.unavailableReason && (
        <p role="note" className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {explained.data.unavailableReason}
        </p>
      )}

      {explained.data && !explained.data.unavailableReason && (
        <div className="space-y-4 text-sm">
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[10rem_1fr]">
            <dt className="text-slate-600">Element</dt>
            <dd className="font-medium">{explained.data.elementCode}</dd>
            <dt className="text-slate-600">Amount</dt>
            <dd className="font-medium">
              {explained.data.amount} {explained.data.currency}
            </dd>
            <dt className="text-slate-600">Rule</dt>
            <dd><code>{explained.data.rule}</code></dd>
            <dt className="text-slate-600">Rule version</dt>
            <dd className="font-mono text-xs">{explained.data.ruleVersionId}</dd>
            <dt className="text-slate-600">Calculated</dt>
            <dd>{explained.data.calculatedAt?.replace('T', ' ').slice(0, 19)}</dd>
          </dl>

          <div>
            <h3 className="mb-1 font-medium">How it got there</h3>
            <table className="w-full">
              <tbody>
                {Object.entries(explained.data.working).map(([key, value]) => (
                  <tr key={key} className="border-b border-slate-100">
                    <td className="py-1 pe-4 text-slate-600">{key}</td>
                    <td className="py-1 font-mono text-xs">{String(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h3 className="mb-1 font-medium">What the rule could see</h3>
            <p className="mb-1 text-slate-600">
              The values available at that point in the calculation. An element reading another
              one sees only what was calculated before it.
            </p>
            <table className="w-full">
              <tbody>
                {Object.entries(explained.data.inputs).map(([key, value]) => (
                  <tr key={key} className="border-b border-slate-100">
                    <td className="py-1 pe-4 text-slate-600">{key}</td>
                    <td className="py-1 font-mono text-xs">{String(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </FormDialog>
  )
}
